/**
 * LinkedTickets component
 *
 * Card component for the Overview tab showing Jira tickets linked to a
 * security issue. Displays ticket key as external link, status badge,
 * sync button with last_synced_at timestamp, and a Jira icon.
 *
 * GEC-28: Shows a warning if Jira is no longer configured but tickets exist.
 */

import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ExternalLink, RefreshCw, Loader2, AlertTriangle } from "lucide-react";
import { useIssueTickets, useSyncIssueTicket } from "@/hooks/useIssueTickets";
import { formatTimeAgo } from "@/utils/securityHelpers";
import { useJiraIntegration } from "@/hooks/useJiraIntegration";
import { JiraLogo } from "@/components/integrations/logos";

interface LinkedTicketsProps {
  issueId: number;
}


export function LinkedTickets({ issueId }: LinkedTicketsProps) {
  const { data: ticketsResponse, isLoading } = useIssueTickets(issueId);
  const syncMutation = useSyncIssueTicket();
  const { data: jiraIntegration } = useJiraIntegration();

  const tickets = ticketsResponse?.data ?? [];
  const isJiraConfigured = jiraIntegration != null;

  // Don't render anything if there are no tickets and we're not loading
  if (!isLoading && tickets.length === 0) {
    return null;
  }

  if (isLoading) {
    return null;
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <JiraLogo className="h-4 w-4 text-blue-600" />
          Linked Tickets
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {tickets.map((ticket) => (
          <div
            key={ticket.id}
            className="flex items-center justify-between p-3 bg-gray-50 rounded border"
          >
            <div className="flex items-center gap-3 min-w-0">
              <JiraLogo className="h-5 w-5 text-blue-600 flex-shrink-0" />
              <div className="min-w-0">
                <a
                  href={ticket.external_ticket_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 text-sm font-medium text-blue-600 hover:text-blue-800 hover:underline"
                >
                  {ticket.external_ticket_id}
                  <ExternalLink className="h-3 w-3" />
                </a>
                <p className="text-xs text-muted-foreground truncate">
                  {ticket.summary}
                </p>
              </div>
            </div>

            <div className="flex items-center gap-2 flex-shrink-0 ml-3">
              {ticket.external_status && (
                <Badge variant="outline" className="text-xs">
                  {ticket.external_status}
                </Badge>
              )}

              <div className="flex items-center gap-1">
                {ticket.last_synced_at && (
                  <span className="text-[10px] text-muted-foreground whitespace-nowrap">
                    {formatTimeAgo(ticket.last_synced_at)}
                  </span>
                )}
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-7 w-7 p-0"
                  onClick={() =>
                    syncMutation.mutate({ ticketId: ticket.id, issueId })
                  }
                  disabled={syncMutation.isPending}
                  title="Sync status from Jira"
                >
                  {syncMutation.isPending ? (
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  ) : (
                    <RefreshCw className="h-3.5 w-3.5" />
                  )}
                </Button>
              </div>
            </div>
          </div>
        ))}

        {/* GEC-28: Warning when tickets exist but Jira is no longer configured */}
        {tickets.length > 0 && !isJiraConfigured && (
          <div className="flex items-start gap-2 rounded-md border border-yellow-200 bg-yellow-50 p-2.5 text-xs text-yellow-800 dark:border-yellow-800 dark:bg-yellow-950 dark:text-yellow-200">
            <AlertTriangle className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" />
            <span>
              Jira integration is no longer configured. Ticket status may be outdated.
            </span>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
